<template>
	<v-card color="rgba(41, 41, 47, 0.8)">
		<v-card-title> {{ $t("EXPORT_AS") }} </v-card-title>
		<v-card-text>
			<v-card-title class="justify-center text-center" style="word-break:break-word;white-space: break-spaces;">
				{{ $t('ExportRoad', { name: name }) }}<br />
				{{ $t('SelectOutputFormat') }}
			</v-card-title>
			<!-- format selection -->
			<v-card-subtitle class="pa-2 mt-2">{{ $t("OUTPUT_FORMAT") }}</v-card-subtitle>
			<v-radio-group v-model="format">
				<v-radio name="format" :label="$t('LandXML')" value="xml"></v-radio>
				<v-radio name="format" v-if="isObj" :label="$t('DXF')" value="dxf"></v-radio>
			</v-radio-group>
			<!-- output destination selection -->
			<v-card-subtitle class="pa-2 mt-2">{{ $t("OUTPUT_DESTINATION") }}</v-card-subtitle>
			<v-radio-group v-model="destination">
				<v-radio name="destination" :label="$t('SAVE_TO_LOCAL')" value="local"></v-radio>
				<v-radio name="destination" :label="$t('Registertodashboard')" value="Smart Construction Dashboard"></v-radio>
				<v-radio v-if="roadObjForExport && roadObjForExport.type === 'road'" name="destination"
					:label="$t('REGISTER_TO_GROUPWARE')" value="Smart Construction Groupware"></v-radio>
			</v-radio-group>
			<v-row class="ma-0">
				<v-col>
					<v-card-actions class="justify-end">
						<v-btn variant="flat" @click="closeDialog" color="red">
							{{ $t("CANCEL") }}
						</v-btn>
						<v-btn variant="flat" @click="exportXmlOrData()" color="primary">
							{{ $t("EXPORT_AS") }}
						</v-btn>
					</v-card-actions>
				</v-col>
			</v-row>
		</v-card-text>
	</v-card>
</template>

<script lang="ts">
import { distanceUnits } from "@/models/siteUnit.types";
import { createDistanceUnitPort } from "@/models/units/distanceUnitPort";
import {
	fetchObjectDxfBlob,
	toDxfDistanceUnit,
	toDxfObjectType,
} from "@/utils/dxfExportApi";
import {
	handleCustomError,
	handleSingleUploadError,
	handleSingleUploadSuccess,
} from "@/utils/notificationUtils";
import axios from "axios";
import { LandXML } from "earthbrain-viewer";
import { mapMutations, useStore } from "vuex";
import { getCookie } from "../../utils/getCookie";
import {
	GoogleAnalyticsClickTypes,
	addClickTypeLog,
} from "../../utils/googleAnalytics.js";

export default {
	name: "ExportLandXMLDialog",
	props: {
		name: String,
		geometry: Object,
		roadObjForExport: Object,
		isObj: Boolean,
	},
	data: () => ({
		store: useStore(),
		destination: "local", // local, dashboard, groupware
		format: "xml", // xml, dxf
	}),
	methods: {
		...mapMutations(["set_snackbar"]),
		/**
		 * v2-server にメッシュを送って DXF Blob を受け取る。頂点は現場単位へ換算してから送る
		 * (サーバは値を変換せず `$INSUNITS` を書くだけ)。
		 */
		async requestDxfBlob(
			objectType: unknown,
			siteDistanceUnit: unknown,
		): Promise<Blob> {
			const site = this.store.state.site;
			const distanceUnit = toDxfDistanceUnit(siteDistanceUnit);
			return fetchObjectDxfBlob({
				siteId: site.site_uuid,
				name: this.name,
				objectType: toDxfObjectType(objectType),
				vertices: this.convertVerticesToUnit(
					this.geometry.vertices,
					distanceUnit,
				),
				indices: this.geometry.indices,
				distanceUnit,
			});
		},
		convertVerticesToUnit(vertices: number[], unit: unknown): number[] {
			const safeUnit =
				Object.values(distanceUnits).find((u) => u === unit) ??
				distanceUnits.METERS;
			const port = createDistanceUnitPort(safeUnit);
			return vertices.map((v) => port.toDisplay(v));
		},
		resetProperty() {
			this.destination = "local";
			this.format = "xml";
		},
		closeDialog() {
			this.$emit("close", false);
		},
		exportXmlOrData() {
			// save to local
			if (this.destination === "local") {
				if (this.format === "xml") {
					// Google Analyticsに連携
					addClickTypeLog(GoogleAnalyticsClickTypes.P_CLICK_OBJECT_EXPORT_XML);
					this.exportLandXML();
				} else if (this.format === "dxf") {
					// DXF Export
					addClickTypeLog(GoogleAnalyticsClickTypes.P_CLICK_OBJECT_EXPORT_DXF);
					this.exportDXF();
				}
			}
			// register to Dashboard
			else if (this.destination === "Smart Construction Dashboard") {
				// Google Analyticsに連携
				if (this.format === "xml") {
					addClickTypeLog(
						GoogleAnalyticsClickTypes.P_CLICK_OBJECT_EXPORT_DASHBOARD,
					);
				} else if (this.format === "dxf") {
					addClickTypeLog(
						GoogleAnalyticsClickTypes.P_CLICK_OBJECT_EXPORT_DASHBOARD_DXF,
					);
				}

				this.uploadToDashboard(this.format);
			}
			// register to Groupware
			else if (this.destination === "Smart Construction Groupware") {
				// Google Analyticsに連携
				if (this.format === "xml") {
					addClickTypeLog(
						GoogleAnalyticsClickTypes.P_CLICK_OBJECT_EXPORT_GROUPWARE,
					);
					this.uploadCenterLineAndDesignFileToGroupwareHandler("xml");
				} else if (this.format === "dxf") {
					addClickTypeLog(
						GoogleAnalyticsClickTypes.P_CLICK_OBJECT_EXPORT_GROUPWARE_DXF,
					);
					this.uploadCenterLineAndDesignFileToGroupwareHandler("dxf");
				}
			}
		},
		async exportLandXML() {
			const site = this.store.state.site;
			const siteDistanceUnit = site?.units?.distance;
			const points = [];
			for (let i = 0; i < this.geometry.vertices.length; i += 3) {
				points.push({
					x: this.geometry.vertices[i],
					y: this.geometry.vertices[i + 1],
					z: this.geometry.vertices[i + 2],
				});
			}
			const landxmlPointsBuffer = [];
			for (const point of points) {
				landxmlPointsBuffer.push(point.y, point.x, point.z); //for landxml, x and y should be flipped
			}
			LandXML.exportMesh(
				this.convertVerticesToUnit(landxmlPointsBuffer, siteDistanceUnit),
				this.geometry.indices,
				site.site_name,
				this.createFileName(new Date(), this.name, site.site_name),
				this.name,
				siteDistanceUnit,
			);
			this.closeDialog();
		},
		async exportDXF() {
			const site = this.store.state.site;
			const siteDistanceUnit = site?.units?.distance;

			// Determine object type
			let objectType = "road"; // default
			if (this.roadObjForExport) {
				objectType = this.roadObjForExport.type || "road";
			}

			this.store.dispatch("updateIsLoading", true);

			try {
				const data = await this.requestDxfBlob(objectType, siteDistanceUnit);

				// Create download link
				const blob = new Blob([data], { type: "application/dxf" });
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.download =
					this.createFileName(new Date(), this.name, site.site_name) + ".dxf";
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);

				handleSingleUploadSuccess(this, "DXF Export", {
					customMessage: `DXF ${this.$t("EXPORT_AS")} ${this.$t("successful")}`,
				});
			} catch (e) {
				handleCustomError(
					this,
					e,
					`DXF ${this.$t("EXPORT_AS")} ${this.$t("failed")}`,
				);
			} finally {
				this.store.dispatch("updateIsLoading", false);
			}

			this.closeDialog();
		},
		async generateDesignFileData(fileType) {
			const site = this.store.state.site;
			const siteDistanceUnit = site?.units?.distance;

			if (fileType === "xml") {
				// Generate LandXML on client side
				const points = [];
				for (let i = 0; i < this.geometry.vertices.length; i += 3) {
					points.push({
						x: this.geometry.vertices[i],
						y: this.geometry.vertices[i + 1],
						z: this.geometry.vertices[i + 2],
					});
				}
				const landxmlPointsBuffer = [];
				for (const point of points) {
					landxmlPointsBuffer.push(point.y, point.x, point.z); // for landxml, x and y should be flipped
				}
				return LandXML.exportMesh(
					this.convertVerticesToUnit(landxmlPointsBuffer, siteDistanceUnit),
					this.geometry.indices,
					site.site_name,
					undefined, // fileName (don't download, just return XML string)
					this.name, // objectName (for XML internal Surface name)
					siteDistanceUnit,
				);
			} else if (fileType === "dxf") {
				// Generate DXF via v2-server
				const objectType = this.roadObjForExport?.type || "road";
				const blob = await this.requestDxfBlob(objectType, siteDistanceUnit);
				return await blob.text();
			}
		},
		async uploadToDashboard(fileType) {
			const site = this.store.state.site;
			this.store.dispatch("updateIsLoading", true);

			try {
				const fileData = await this.generateDesignFileData(fileType);
				const fileName = this.createFileName(
					new Date(),
					this.name,
					site.site_name,
				);

				await axios.post(
					`${import.meta.env.VITE_API_BASE}/dashboard/designFile`,
					{
						fileType: fileType,
						[`${fileType}Data`]: fileData,
						[`${fileType}Name`]: fileName,
						siteId: site.site_uuid,
					},
					{
						headers: {
							"Content-Type": "application/json",
						},
					},
				);

				handleSingleUploadSuccess(this, `${this.$t("Registertodashboard")}`, {
					customMessage: `${fileType.toUpperCase()} ${this.$t("Registertodashboard")} ${this.$t("successful")}`,
				});
			} catch (e) {
				handleCustomError(
					this,
					e,
					`${fileType.toUpperCase()} ${this.$t("Registertodashboard")} ${this.$t("failed")}`,
				);
			} finally {
				this.store.dispatch("updateIsLoading", false);
			}

			this.closeDialog();
		},
		async uploadCenterLineAndDesignFileToGroupwareHandler(fileType) {
			try {
				this.store.dispatch("updateIsLoading", true);
				const formData = await this.createCenterlineAndDesignFileData(fileType);
				const response = await this.uploadCenterLineAndDesignFileToGroupware(
					formData,
					fileType,
				);

				if (response.status === 200) {
					this.set_snackbar({
						text: `${this.$t("UPLOAD")} ${this.$t("successful")}`,
						color: "rgba(0, 153, 0, 0.72)",
					});
				} else {
					throw new Error("upload failed");
				}
			} catch (error) {
				console.error("error:", error);
				this.set_snackbar({
					text: this.$t("UPLOAD_ERROR"),
					color: "rgba(153, 0, 0, 0.72)",
				});
			} finally {
				this.store.dispatch("updateIsLoading", false);
				this.closeDialog();
			}
		},
		async createCenterlineAndDesignFileData(fileType) {
			if (!this.roadObjForExport) throw new Error("Object is not defined");

			const formData = new FormData();
			formData.append("road_name", this.roadObjForExport.name);

			// get positions from viewer
			const positions =
				await this.store.getters.viewer.recalculateCenterLinePositions(
					this.roadObjForExport.id,
				);
			const jsonFile = new Blob([JSON.stringify(positions)], {
				type: "application/json",
			});
			formData.append("json", jsonFile, "centerline.json");

			// create design file (XML or DXF) from geometry
			const site = this.store.state.site;
			const siteDistanceUnit = site?.units?.distance;
			let objectType = "road"; // default
			if (this.roadObjForExport) {
				objectType = this.roadObjForExport.type || "road";
			}

			if (fileType === "xml") {
				// create LandXML from geometry
				const points = [];
				for (let i = 0; i < this.geometry.vertices.length; i += 3) {
					points.push({
						x: this.geometry.vertices[i],
						y: this.geometry.vertices[i + 1],
						z: this.geometry.vertices[i + 2],
					});
				}
				const landxmlPointsBuffer = [];
				for (const point of points) {
					landxmlPointsBuffer.push(point.y, point.x, point.z);
				}
				const xml = LandXML.exportMesh(
					this.convertVerticesToUnit(landxmlPointsBuffer, siteDistanceUnit),
					this.geometry.indices,
					site.site_name,
					undefined,
					undefined,
					siteDistanceUnit,
				);
				const xmlFile = new Blob([xml], { type: "application/xml" });
				formData.append("xml", xmlFile, "design.xml");
			} else if (fileType === "dxf") {
				// create DXF via v2-server
				const dxfBlob = await this.requestDxfBlob(objectType, siteDistanceUnit);
				const dxfFile = new Blob([dxfBlob], { type: "application/dxf" });
				formData.append("dxf", dxfFile, "design.dxf");
			}

			return formData;
		},
		async uploadCenterLineAndDesignFileToGroupware(formData, fileType) {
			const endpoint = fileType === "xml" ? "centerLine" : "centerlineDxf";
			return await axios.post(
				`${import.meta.env.VITE_API_BASE}/linearFile/${this.$route.query.siteId}/${endpoint}`,
				formData,
				{
					headers: {
						Authorization: `Bearer ${getCookie("access_token")}`,
						"Content-Type": "multipart/form-data",
					},
				},
			);
		},
		createFileName(date, objName, siteName) {
			return (
				[
					date.getFullYear(),
					this.padSingleDigit(date.getMonth() + 1),
					this.padSingleDigit(date.getDate()),
				].join("-") +
				" " +
				[
					this.padSingleDigit(date.getHours()),
					this.padSingleDigit(date.getMinutes()),
					this.padSingleDigit(date.getSeconds()),
				].join("_") +
				`_${siteName}_${objName}`
			);
		},
		padSingleDigit(num) {
			return num.toString().padStart(2, "0");
		},
	},
};
</script>